import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { Mail, PenSquare, Wifi, WifiOff } from 'lucide-react';
import EmailSidebar from '../../components/Marketing/email/EmailSidebar';
import EmailListPanel from '../../components/Marketing/email/EmailListPanel';
import ComposeModal from '../../components/Marketing/email/ComposeModal';
import EmailReadPanel from '../../components/Marketing/email/EmailReadPanel';
import TemplatesPanel from '../../components/Marketing/email/TemplatesPanel';
import ScheduleSendModal from '../../components/Marketing/email/ScheduleSendModal';
import SignatureManagerModal from '../../components/Marketing/email/SignatureManagerModal';
import TemplateFormModal from '../../components/Marketing/email/TemplateFormModal';
import EmailSettingsPanel from '../../components/Marketing/email/EmailSettingsPanel';
import EmailAnalyticsPanel from '../../components/Marketing/email/EmailAnalyticsPanel';
import { meEmptyStateClass, mePageWrapClass, mePrimaryBtnClass, meShellClass } from '../../components/Marketing/email/marketingEmailUi';
import type {
  ComposeFormState,
  EmailSignature,
  MarketingEmailCampaign,
  MarketingEmailFolder,
  MarketingEmailSettings,
  MarketingEmailTemplate,
} from '../../models/MasterAdmin/marketingEmail';
import { DEFAULT_FROM_EMAILS, DEFAULT_SENDER_EMAIL } from '../../models/MasterAdmin/marketingEmail';
import {
  createCampaign,
  createTemplate,
  deleteCampaign,
  bulkDeleteCampaigns,
  emptyTrash,
  deleteTemplate,
  fetchBrevoStatus,
  fetchCampaigns,
  fetchContacts,
  fetchReplyDraft,
  fetchSettings,
  fetchSignatures,
  fetchTemplates,
  saveSignatures,
  sendCampaign,
  retryCampaign,
  toggleCampaignStar,
  updateCampaign,
  updateTemplate,
} from '../../services/MasterAdmin/marketingEmailService';

const PAGE_SIZE = 20;

function emptyCompose(signatures: EmailSignature[], settings?: MarketingEmailSettings | null): ComposeFormState {
  const def = signatures.find((s) => s.isDefault) ?? signatures[0];
  return {
    from: settings?.defaultFromEmail || DEFAULT_SENDER_EMAIL,
    to: [],
    cc: [],
    bcc: [],
    replyTo: '',
    subject: '',
    htmlBody: '',
    audienceSegment: 'USERS_WITH_MARKETING_OPT_IN',
    recipientMode: 'custom',
    sendIndividually: true,
    includeSignature: true,
    signatureId: def?.id,
    attachments: [],
  };
}

const MarketingEmailsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<MarketingEmailCampaign[]>([]);
  const [campaignTotal, setCampaignTotal] = useState(0);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignTotalPages, setCampaignTotalPages] = useState(1);
  const [templates, setTemplates] = useState<MarketingEmailTemplate[]>([]);
  const [signatures, setSignatures] = useState<EmailSignature[]>([]);
  const [emailSettings, setEmailSettings] = useState<MarketingEmailSettings | null>(null);
  const [contactSuggestions, setContactSuggestions] = useState<string[]>([]);
  const [brevoConnected, setBrevoConnected] = useState<boolean | null>(null);
  const [brevoStatusMessage, setBrevoStatusMessage] = useState<string | null>(null);

  const [activeFolder, setActiveFolder] = useState<MarketingEmailFolder>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const [composeOpen, setComposeOpen] = useState(false);
  const [composeEditorKey, setComposeEditorKey] = useState(0);
  const [composeForm, setComposeForm] = useState<ComposeFormState>(emptyCompose([]));
  const [editingId, setEditingId] = useState<string | null>(null);

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [retryCampaignId, setRetryCampaignId] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MarketingEmailTemplate | null>(null);
  const [sending, setSending] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [folderCounts, setFolderCounts] = useState<Partial<Record<MarketingEmailFolder, number>>>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadCampaigns = useCallback(
    async (folder: MarketingEmailFolder, search: string, page: number) => {
      setLoading(true);
      try {
        const res = await fetchCampaigns({
          folder: folder === 'all' ? undefined : folder,
          search: search.trim() || undefined,
          page,
          limit: PAGE_SIZE,
        });
        setCampaigns(res.data.campaigns);
        setCampaignTotal(res.data.total);
        setCampaignPage(res.data.page);
        setCampaignTotalPages(res.data.totalPages);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadFolderCounts = useCallback(async () => {
    try {
      const [draftsRes, scheduledRes, trashRes] = await Promise.all([
        fetchCampaigns({ folder: 'drafts', limit: 1, page: 1 }),
        fetchCampaigns({ folder: 'scheduled', limit: 1, page: 1 }),
        fetchCampaigns({ folder: 'trash', limit: 1, page: 1 }),
      ]);
      setFolderCounts({
        drafts: draftsRes.data.total,
        scheduled: scheduledRes.data.total,
        trash: trashRes.data.total,
      });
    } catch {
      // non-critical
    }
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      const res = await fetchContacts({ limit: 500 });
      setContactSuggestions(res.data.contacts.map((c) => c.email));
    } catch {
      setContactSuggestions([]);
    }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const [tplRes, sigRes, settingsRes] = await Promise.all([
        fetchTemplates(),
        fetchSignatures(),
        fetchSettings().catch(() => null),
      ]);
      setTemplates(tplRes.data.templates);
      setSignatures(sigRes.data.signatures);
      if (settingsRes?.data) setEmailSettings(settingsRes.data);

      try {
        const brevoRes = await fetchBrevoStatus();
        setBrevoConnected(brevoRes.data.connected);
        setBrevoStatusMessage(brevoRes.message ?? null);
      } catch {
        setBrevoConnected(false);
        setBrevoStatusMessage('Could not check Brevo connection');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load marketing emails');
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadCampaigns(activeFolder, searchQuery, campaignPage),
      loadFolderCounts(),
      loadMeta(),
    ]);
  }, [activeFolder, searchQuery, campaignPage, loadCampaigns, loadFolderCounts, loadMeta]);

  useEffect(() => {
    loadMeta();
    loadFolderCounts();
    loadContacts();
  }, [loadMeta, loadFolderCounts, loadContacts]);

  useEffect(() => {
    if (activeFolder === 'templates' || activeFolder === 'analytics' || activeFolder === 'settings') return;
    loadCampaigns(activeFolder, searchQuery, campaignPage);
    setSelectedIds([]);
  }, [activeFolder, searchQuery, campaignPage, loadCampaigns]);

  useEffect(() => {
    if (signatures.length && !composeOpen) {
      setComposeForm((prev) => ({ ...prev, signatureId: prev.signatureId ?? signatures.find((s) => s.isDefault)?.id }));
    }
  }, [signatures, composeOpen]);

  const selectedEmail = useMemo(
    () => campaigns.find((c) => c.id === selectedId) ?? null,
    [campaigns, selectedId]
  );

  const openCompose = (prefill?: Partial<ComposeFormState>, editId?: string) => {
    setComposeEditorKey((k) => k + 1);
    setComposeForm({ ...emptyCompose(signatures, emailSettings), ...prefill });
    setEditingId(editId ?? null);
    setComposeOpen(true);
    setSelectedId(null);
    loadContacts();
  };

  const handleReply = async (email: MarketingEmailCampaign, mode: 'reply' | 'replyAll') => {
    setReplyLoading(true);
    try {
      const res = await fetchReplyDraft(email.id, mode, email.from);
      const draft = res.data;
      openCompose({
        from: draft.from || email.from,
        to: draft.to || [],
        cc: draft.cc || [],
        bcc: draft.bcc || [],
        replyTo: draft.replyTo || '',
        subject: draft.subject || '',
        htmlBody: draft.htmlBody || '',
        audienceSegment: 'CUSTOM_LIST',
        recipientMode: 'custom',
        sendIndividually: true,
        includeSignature: draft.includeSignature !== false,
        signatureId: draft.signatureId || signatures.find((s) => s.isDefault)?.id,
        attachments: [],
        parentCampaignId: draft.parentCampaignId || email.id,
        inReplyTo: draft.inReplyTo,
        references: draft.references,
        replyMode: mode,
      });
      toast.success(mode === 'replyAll' ? 'Reply all draft ready — same thread' : 'Reply draft ready — same thread');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to open reply');
    } finally {
      setReplyLoading(false);
    }
  };

  const closeCompose = () => {
    setComposeOpen(false);
    setEditingId(null);
    setComposeForm(emptyCompose(signatures, emailSettings));
  };

  const getSendErrorMessage = (e: unknown): string => {
    if (e && typeof e === 'object' && 'response' in e) {
      const data = (e as { response?: { data?: { message?: string } } }).response?.data;
      if (data?.message) return data.message;
    }
    if (e instanceof Error && e.message && !e.message.startsWith('Request failed with status code')) {
      return e.message;
    }
    return 'Send failed — check browser Network tab or Cloud Run logs for details';
  };

  const persistAndSend = async (scheduledAt?: string) => {
    setSending(true);
    try {
      if (composeForm.recipientMode === 'custom' && !composeForm.to.length) {
        toast.error('Add at least one email in the To field');
        return;
      }

      let campaignId = editingId;

      if (campaignId) {
        await updateCampaign(campaignId, composeForm);
      } else {
        const created = await createCampaign({ ...composeForm, action: 'draft' });
        campaignId = created.data.id;
      }

      const result = await sendCampaign(campaignId!, scheduledAt);
      toast.success(result.message || (scheduledAt ? 'Campaign scheduled' : 'Campaign sent'));
      closeCompose();
      setCampaignPage(1);
      setActiveFolder(scheduledAt ? 'scheduled' : 'sent');
      setSelectedId(result.data.id);
      await loadFolderCounts();
      await loadCampaigns(scheduledAt ? 'scheduled' : 'sent', searchQuery, 1);
    } catch (e) {
      toast.error(getSendErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      if (editingId) {
        await updateCampaign(editingId, { ...composeForm, action: 'draft' });
      } else {
        await createCampaign({ ...composeForm, action: 'draft' });
      }
      toast.success('Draft saved');
      closeCompose();
      setSearchQuery('');
      setCampaignPage(1);
      setActiveFolder('drafts');
      await loadFolderCounts();
      await loadCampaigns('drafts', '', 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save draft');
    }
  };

  const handleApplyTemplate = (tpl: MarketingEmailTemplate) => {
    if (composeOpen) {
      setComposeForm((prev) => ({ ...prev, subject: tpl.subject, htmlBody: tpl.htmlBody }));
    } else {
      openCompose({ subject: tpl.subject, htmlBody: tpl.htmlBody });
    }
    toast.success(`Template "${tpl.name}" applied`);
  };

  const handleSaveTemplate = async (tpl: MarketingEmailTemplate) => {
    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, tpl);
      } else {
        await createTemplate(tpl);
      }
      toast.success(editingTemplate ? 'Template updated' : 'Template created');
      const res = await fetchTemplates();
      setTemplates(res.data.templates);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success('Template deleted');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete template');
    }
  };

  const handleToggleStar = async (id: string) => {
    try {
      const res = await toggleCampaignStar(id);
      setCampaigns((prev) => prev.map((c) => (c.id === id ? res.data : c)));
    } catch {
      toast.error('Failed to update star');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await deleteCampaign(id);
      if (selectedId === id) setSelectedId(null);
      if (editingId === id) closeCompose();
      setSelectedIds((prev) => prev.filter((item) => item !== id));
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      await loadFolderCounts();
      await loadCampaigns(activeFolder, searchQuery, campaignPage);
      toast.success(res.message || (activeFolder === 'trash' ? 'Permanently deleted' : 'Moved to trash'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const handleSelectAllOnPage = () => {
    setSelectedIds(campaigns.map((c) => c.id));
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;

    const inTrash = activeFolder === 'trash';
    const message = inTrash
      ? `Permanently delete ${selectedIds.length} selected campaign${selectedIds.length === 1 ? '' : 's'}? This cannot be undone.`
      : `Move ${selectedIds.length} selected campaign${selectedIds.length === 1 ? '' : 's'} to trash?`;

    if (!window.confirm(message)) return;

    setBulkDeleting(true);
    try {
      const res = await bulkDeleteCampaigns(selectedIds);
      if (selectedIds.includes(selectedId || '')) setSelectedId(null);
      if (editingId && selectedIds.includes(editingId)) closeCompose();
      setSelectedIds([]);
      await loadFolderCounts();
      await loadCampaigns(activeFolder, searchQuery, campaignPage);
      toast.success(res.message || (inTrash ? 'Deleted permanently' : 'Moved to trash'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleEmptyTrash = async () => {
    const count = campaignTotal || folderCounts.trash || 0;
    if (!count) return;

    const message = `Permanently delete all ${count} campaign${count === 1 ? '' : 's'} in trash? This cannot be undone.`;
    if (!window.confirm(message)) return;

    setBulkDeleting(true);
    try {
      const res = await emptyTrash();
      setSelectedId(null);
      setSelectedIds([]);
      if (editingId) closeCompose();
      await loadFolderCounts();
      await loadCampaigns('trash', searchQuery, 1);
      setCampaignPage(1);
      toast.success(res.message || 'Trash emptied');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to empty trash');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleRetrySend = async (campaignId: string) => {
    setRetrying(true);
    try {
      const result = await retryCampaign(campaignId, { mode: 'send_now' });
      toast.success(result.message || 'Campaign sent on retry');
      setSelectedId(result.data.id);
      setActiveFolder('sent');
      await loadFolderCounts();
      await loadCampaigns('sent', searchQuery, 1);
      setCampaignPage(1);
    } catch (e) {
      toast.error(getSendErrorMessage(e));
      await loadCampaigns(activeFolder, searchQuery, campaignPage);
    } finally {
      setRetrying(false);
    }
  };

  const openRetrySchedule = (campaignId: string) => {
    setRetryCampaignId(campaignId);
    setScheduleModalOpen(true);
  };

  const handleRetryScheduleConfirm = async (scheduledAt: string) => {
    if (!retryCampaignId) return;
    setRetrying(true);
    try {
      const result = await retryCampaign(retryCampaignId, { mode: 'schedule', scheduledAt });
      toast.success(result.message || 'Campaign rescheduled');
      setScheduleModalOpen(false);
      setRetryCampaignId(null);
      setSelectedId(result.data.id);
      setActiveFolder('scheduled');
      await loadFolderCounts();
      await loadCampaigns('scheduled', searchQuery, 1);
      setCampaignPage(1);
    } catch (e) {
      toast.error(getSendErrorMessage(e));
    } finally {
      setRetrying(false);
    }
  };

  const handleEditDraft = (email: MarketingEmailCampaign) => {
    openCompose(
      {
        from: email.from,
        to: email.to,
        cc: email.cc,
        bcc: email.bcc,
        replyTo: email.replyTo ?? '',
        subject: email.subject,
        htmlBody: email.htmlBody,
        audienceSegment: email.audienceSegment,
        recipientMode: email.recipientMode,
        sendIndividually: email.sendIndividually,
        includeSignature: email.includeSignature,
        signatureId: email.signatureId,
        attachments: email.attachments,
      },
      email.id
    );
  };

  const showList = activeFolder !== 'templates' && activeFolder !== 'analytics' && activeFolder !== 'settings';

  return (
    <div className={mePageWrapClass}>
      <Toaster position="top-right" />

      {brevoConnected !== null && (
        <div className={`mb-2 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${
          brevoConnected
            ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
        }`}>
          {brevoConnected ? <Wifi className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <WifiOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
          <div>
            <p className="font-medium">{brevoConnected ? 'Brevo connected' : 'Brevo not connected'}</p>
            {!brevoConnected && brevoStatusMessage && (
              <p className="mt-0.5 opacity-90">{brevoStatusMessage}</p>
            )}
            {!brevoConnected && !brevoStatusMessage && (
              <p className="mt-0.5 opacity-90">
                Ensure <code className="rounded bg-black/5 px-1 dark:bg-white/10">BREVO_API_KEY_PROD</code> exists in Secret Manager, or set <code className="rounded bg-black/5 px-1 dark:bg-white/10">BREVO_API_KEY</code> in backend <code className="rounded bg-black/5 px-1 dark:bg-white/10">.env</code>. Local dev also needs <code className="rounded bg-black/5 px-1 dark:bg-white/10">gcloud auth application-default login</code>.
              </p>
            )}
          </div>
        </div>
      )}

      <div className={meShellClass}>
        <EmailSidebar
          activeFolder={activeFolder}
          onFolderChange={(folder) => {
            setActiveFolder(folder);
            setSelectedId(null);
            setCampaignPage(1);
            setSearchQuery('');
            setSelectedIds([]);
            if (folder === 'templates' || folder === 'analytics' || folder === 'settings') setComposeOpen(false);
          }}
          onCompose={() => openCompose()}
          counts={folderCounts}
        />

        {activeFolder === 'templates' ? (
          <TemplatesPanel
            templates={templates}
            onSelect={handleApplyTemplate}
            onCreate={() => { setEditingTemplate(null); setTemplateFormOpen(true); }}
            onEdit={(tpl) => { setEditingTemplate(tpl); setTemplateFormOpen(true); }}
            onDelete={handleDeleteTemplate}
            onClose={() => setActiveFolder('all')}
          />
        ) : activeFolder === 'analytics' ? (
          <EmailAnalyticsPanel onClose={() => setActiveFolder('all')} />
        ) : activeFolder === 'settings' ? (
          <EmailSettingsPanel
            signatures={signatures}
            onManageSignatures={() => setSignatureModalOpen(true)}
            onClose={() => setActiveFolder('all')}
          />
        ) : (
          <>
            {showList && (
              <EmailListPanel
                emails={campaigns}
                selectedId={selectedId}
                selectedIds={selectedIds}
                activeFolder={activeFolder}
                searchQuery={searchQuery}
                onSearchChange={(q) => {
                  setSearchQuery(q);
                  setCampaignPage(1);
                  setSelectedIds([]);
                }}
                onSelect={(id) => setSelectedId(id)}
                onToggleSelect={handleToggleSelect}
                onSelectAll={handleSelectAllOnPage}
                onClearSelection={() => setSelectedIds([])}
                onBulkDelete={handleBulkDelete}
                onEmptyTrash={activeFolder === 'trash' ? handleEmptyTrash : undefined}
                onToggleStar={handleToggleStar}
                onRefresh={refreshAll}
                loading={loading}
                bulkDeleting={bulkDeleting}
                page={campaignPage}
                totalPages={campaignTotalPages}
                total={campaignTotal}
                onPageChange={(p) => {
                  setCampaignPage(p);
                  setSelectedIds([]);
                }}
              />
            )}

            {!composeOpen && selectedEmail && (
              <EmailReadPanel
                email={selectedEmail}
                onToggleStar={() => handleToggleStar(selectedEmail.id)}
                onDelete={() => handleDelete(selectedEmail.id)}
                onEdit={
                  selectedEmail.status === 'draft'
                    ? () => handleEditDraft(selectedEmail)
                    : undefined
                }
                onDuplicate={() =>
                  openCompose({
                    subject: selectedEmail.subject,
                    htmlBody: selectedEmail.htmlBody,
                    audienceSegment: selectedEmail.audienceSegment,
                    recipientMode: selectedEmail.recipientMode,
                    sendIndividually: selectedEmail.sendIndividually,
                    to: selectedEmail.to,
                    includeSignature: selectedEmail.includeSignature,
                    signatureId: selectedEmail.signatureId,
                  })
                }
                onRetrySend={
                  selectedEmail.status === 'failed'
                    ? () => handleRetrySend(selectedEmail.id)
                    : undefined
                }
                onRetrySchedule={
                  selectedEmail.status === 'failed'
                    ? () => openRetrySchedule(selectedEmail.id)
                    : undefined
                }
                onReply={() => handleReply(selectedEmail, 'reply')}
                onReplyAll={() => handleReply(selectedEmail, 'replyAll')}
                retrying={retrying}
                replyLoading={replyLoading}
              />
            )}

            {!composeOpen && !selectedEmail && (
              <div className={meEmptyStateClass + ' bg-white dark:bg-gray-900'}>
                <div className="rounded-2xl bg-gray-100 p-5 dark:bg-gray-800">
                  <Mail className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">No campaign selected</p>
                  <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Pick a campaign from the list or compose a new marketing email via Brevo.
                  </p>
                </div>
                <button type="button" onClick={() => openCompose()} className={mePrimaryBtnClass}>
                  <PenSquare className="h-4 w-4" />
                  Compose
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ComposeModal
        open={composeOpen}
        form={composeForm}
        templates={templates}
        signatures={signatures}
        signatureImages={emailSettings?.signatureImages}
        fromEmails={emailSettings?.fromEmails || DEFAULT_FROM_EMAILS}
        contactSuggestions={contactSuggestions}
        onChange={(patch: Partial<ComposeFormState>) => setComposeForm((prev) => ({ ...prev, ...patch }))}
        onSend={() => persistAndSend()}
        onSchedule={() => setScheduleModalOpen(true)}
        onSaveDraft={handleSaveDraft}
        onDiscard={closeCompose}
        onDelete={editingId ? () => handleDelete(editingId) : undefined}
        editingId={editingId}
        onClose={closeCompose}
        onManageSignatures={() => setSignatureModalOpen(true)}
        onCreateTemplate={() => { setEditingTemplate(null); setTemplateFormOpen(true); }}
        sending={sending}
        editorKey={composeEditorKey}
      />

      <ScheduleSendModal
        open={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
          setRetryCampaignId(null);
        }}
        title={retryCampaignId ? 'Reschedule failed campaign' : undefined}
        onConfirm={(scheduledAt) => {
          setScheduleModalOpen(false);
          if (retryCampaignId) {
            handleRetryScheduleConfirm(scheduledAt);
          } else {
            persistAndSend(scheduledAt);
          }
        }}
      />

      <SignatureManagerModal
        open={signatureModalOpen}
        signatures={signatures}
        signatureImages={emailSettings?.signatureImages}
        onClose={() => setSignatureModalOpen(false)}
        onSave={async (next) => {
          const res = await saveSignatures(next);
          setSignatures(res.data.signatures);
          toast.success('Signatures saved');
        }}
      />

      <TemplateFormModal
        open={templateFormOpen}
        template={editingTemplate}
        initialSubject={editingTemplate ? undefined : composeForm.subject}
        initialHtmlBody={editingTemplate ? undefined : composeForm.htmlBody}
        onClose={() => { setTemplateFormOpen(false); setEditingTemplate(null); }}
        onSave={handleSaveTemplate}
      />
    </div>
  );
};

export default MarketingEmailsPage;
